import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

export default class extends Controller {
  static values = {
    organizationId: Number,
    resourceType: String // "calls", "customers", "tickets", "agents"
  };

  connect() {
    if (!this.hasOrganizationIdValue) {
      console.warn("RealtimeIndex: No organization ID provided");
      return;
    }

    if (!this.hasResourceTypeValue) {
      console.warn("RealtimeIndex: No resource type provided");
      return;
    }

    // Subscribe to organization channel for updates
    this.subscription = consumer.subscriptions.create(
      {
        channel: "OrganizationChannel",
        organization_id: this.organizationIdValue
      },
      {
        received: (data) => {
          this.handleMessage(data);
        },
        connected: () => {},
        disconnected: () => {}
      }
    );
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  handleMessage(data) {
    // Route to appropriate handler based on resource type
    switch (this.resourceTypeValue) {
    case "calls":
      this.handleCallUpdate(data);
      break;
    case "customers":
      this.handleCustomerUpdate(data);
      break;
    case "tickets":
      this.handleTicketUpdate(data);
      break;
    case "agents":
      this.handleAgentUpdate(data);
      break;
    }
  }

  handleCallUpdate(data) {
    switch (data.type) {
    case "call_status_updated":
    case "call_ended":
      this.updateCallRow(data);
      break;
    case "call_created":
      this.addCallRow(data);
      break;
    }
  }

  handleCustomerUpdate(data) {
    switch (data.type) {
    case "customer_created":
      this.addCustomerRow(data);
      break;
    case "customer_updated":
      this.updateCustomerRow(data);
      break;
    }
  }

  handleTicketUpdate(data) {
    switch (data.type) {
    case "ticket_created":
      this.addTicketRow(data);
      break;
    case "ticket_updated":
      this.updateTicketRow(data);
      break;
    case "ticket_status_changed":
      this.updateTicketRow(data);
      break;
    }
  }

  handleAgentUpdate(data) {
    switch (data.type) {
    case "agent_created":
    case "agent_invited":
      this.addAgentRow(data);
      break;
    case "agent_updated":
      this.updateAgentRow(data);
      break;
    }
  }

  // Call-specific updates
  updateCallRow(data) {
    const callId = data.call_id;
    if (!callId) return;

    const callRow = document.querySelector(`tr[data-call-id="${callId}"]`);
    if (!callRow) {
      // Call might not be in current view (filtered out, paginated, etc.)
      return;
    }

    // Update status badge
    if (data.status) {
      const statusCell = callRow.querySelector("[data-call-status]");
      if (statusCell) {
        const statusBadge = statusCell.querySelector("span");
        if (statusBadge) {
          statusBadge.className = "inline-flex px-2 text-xs font-semibold leading-5 rounded-full";
          
          if (data.status === "completed") {
            statusBadge.classList.add("text-green-800", "bg-green-100", "dark:bg-green-900/30", "dark:text-green-300");
          } else if (data.status === "failed" || data.status === "missed") {
            statusBadge.classList.add("text-red-800", "bg-red-100", "dark:bg-red-900/30", "dark:text-red-300");
          } else {
            statusBadge.classList.add("text-yellow-800", "bg-yellow-100", "dark:bg-yellow-900/30", "dark:text-yellow-300");
          }
          
          statusBadge.textContent = data.status.charAt(0).toUpperCase() + data.status.slice(1).replace("_", " ");
        }
      }
    }

    // Update duration
    if (data.duration_seconds !== undefined) {
      const durationCell = callRow.querySelector("[data-call-duration]");
      if (durationCell) {
        const minutes = (data.duration_seconds / 60.0).toFixed(1);
        durationCell.textContent = `${minutes}m`;
      }
    }
  }

  addCallRow(_data) {
    // For now, just refresh the page or show a notification
    // In the future, we could prepend the new call to the table
    // Optionally trigger a page refresh or Turbo Stream update
  }

  // Customer-specific updates
  addCustomerRow(_data) {
    // Could prepend to table or refresh
  }

  updateCustomerRow(data) {
    const customerId = data.customer_id;
    if (!customerId) return;

    const customerRow = document.querySelector(`tr[data-customer-id="${customerId}"]`);
    if (!customerRow) return;

    // Update customer name if provided
    if (data.customer_name) {
      const nameCell = customerRow.querySelector("[data-customer-name]");
      if (nameCell) {
        nameCell.textContent = data.customer_name;
      }
    }
  }

  // Ticket-specific updates
  addTicketRow(_data) {
    // Could prepend to table or refresh
  }

  updateTicketRow(data) {
    const ticketId = data.ticket_id;
    if (!ticketId) return;

    const ticketRow = document.querySelector(`tr[data-ticket-id="${ticketId}"]`);
    if (!ticketRow) return;

    // Update status badge
    if (data.status) {
      const statusCell = ticketRow.querySelector("[data-ticket-status]");
      if (statusCell) {
        const statusBadge = statusCell.querySelector("span");
        if (statusBadge) {
          statusBadge.className = "inline-flex px-2 text-xs font-semibold leading-5 rounded-full";
          
          if (data.status === "resolved" || data.status === "closed") {
            statusBadge.classList.add("text-green-800", "bg-green-100", "dark:bg-green-900/30", "dark:text-green-300");
          } else if (data.status === "open") {
            statusBadge.classList.add("text-blue-800", "bg-blue-100", "dark:bg-blue-900/30", "dark:text-blue-300");
          } else {
            statusBadge.classList.add("text-yellow-800", "bg-yellow-100", "dark:bg-yellow-900/30", "dark:text-yellow-300");
          }
          
          statusBadge.textContent = data.status.charAt(0).toUpperCase() + data.status.slice(1);
        }
      }
    }
  }

  // Agent-specific updates
  addAgentRow(_data) {
    // Could prepend to table or refresh
  }

  updateAgentRow(data) {
    const agentId = data.agent_id || data.membership_id;
    if (!agentId) return;

    const agentRow = document.querySelector(`tr[data-agent-id="${agentId}"]`);
    if (!agentRow) return;

    // Update agent status if provided
    if (data.status) {
      const statusCell = agentRow.querySelector("[data-agent-status]");
      if (statusCell) {
        statusCell.textContent = data.status;
      }
    }
  }
}

